import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  Today as TodayIcon,
  Timeline as TimelineIcon,
  ViewTimeline as ViewTimelineIcon,
  ReceiptLong as ReceiptLongIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Upcoming as UpcomingIcon,
  DoneAll as DoneAllIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { billsApi } from '../../services/smartBillApi';

const RANGE_OPTIONS = [
  { id: '7', label: '7 Days' },
  { id: '15', label: '15 Days' },
  { id: '30', label: '30 Days' },
  { id: '60', label: '60 Days' },
  { id: 'all', label: 'All' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const toISTDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const formatDisplayDate = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00+05:30`);
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const computeServiceCharges = (bill) => {
  if (!bill?.services || !Array.isArray(bill.services)) {
    return Number(bill?.total_amount ?? bill?.totalAmount ?? bill?.amount ?? 0);
  }

  return bill.services.reduce((sum, service) => {
    const quantity = Number(service.quantity || 1);
    const challana = Number(service.challana_amount || service.challanaAmount || service.challana || 0);
    const serviceCharge =
      Number(service.service_charge || service.serviceCharge || service.price || service.service_price || 0) *
      quantity;
    const total = Number(service.total || 0);

    if (serviceCharge > 0) {
      return sum + (serviceCharge - challana);
    }

    if (total > 0) {
      return sum + (total - challana);
    }

    return sum;
  }, 0);
};

const CounterSalesDatewise = () => {
  const [rawBills, setRawBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('30');
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  const navigate = useNavigate();

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await billsApi.getAll({ limit: 10000 });

      if (!response?.success) {
        throw new Error(response?.message || 'Unable to load bills data');
      }

      setRawBills(response.data || []);
    } catch (err) {
      console.error('Failed to load bills for counter sales view:', err);
      setError(err.message || 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, []);

  const dailyData = useMemo(() => {
    if (!Array.isArray(rawBills) || rawBills.length === 0) return [];

    const map = new Map();

    rawBills.forEach((bill) => {
      // Exclude plain cancelled bills; keep cancel-with-fee (processing fee retained).
      const workStatus = (bill.work_status || bill.workStatus || bill.payment_status || bill.status || '').toLowerCase();
      const feeRetained = Number(bill.processing_fee_retained ?? bill.processingFeeRetained ?? 0);
      if (workStatus === 'cancelled' && feeRetained <= 0) return;

      // Advance bills count in counter sales only after completion (sales_counted_at).
      const isAdvance = !!(bill.is_advance ?? bill.isAdvance);
      const salesCountedAt = bill.sales_counted_at ?? bill.salesCountedAt ?? null;
      if (isAdvance && !salesCountedAt) return;

      // Calculate service charges first to check if bill has valid service charges
      const serviceCharges = computeServiceCharges(bill);
      
      // Only include bills that have services with service_charge > 0 (matching backend filter)
      if (bill.services && Array.isArray(bill.services)) {
        const hasValidServiceCharge = bill.services.some(service => {
          const serviceCharge = Number(service.service_charge || service.serviceCharge || service.price || service.service_price || 0);
          return serviceCharge > 0;
        });
        if (!hasValidServiceCharge) return;
      } else if (serviceCharges === 0) {
        // If no services array and service charges is 0, skip (matching backend behavior)
        return;
      }

      const dateKey = toISTDateKey(
        salesCountedAt ||
          bill.bill_date ||
          bill.billDate ||
          bill.created_at ||
          bill.createdAt ||
          bill.updated_at ||
          bill.updatedAt
      );
      if (!dateKey) return;

      if (!map.has(dateKey)) {
        map.set(dateKey, {
          dateKey,
          totalBills: 0,
          totalServiceCharges: 0,
          grossAmount: 0,
          totalPaid: 0,
          totalDue: 0,
          statusCounts: {
            completed: 0,
            pending: 0,
            under_processing: 0,
            cancelled: 0,
            other: 0,
          },
          services: {},
        });
      }

      const entry = map.get(dateKey);
      entry.totalBills += 1;
      entry.grossAmount += Number(bill.total_amount ?? bill.totalAmount ?? bill.amount ?? 0);
      entry.totalServiceCharges += serviceCharges;
      entry.totalPaid += Number(bill.paid_amount ?? bill.paidAmount ?? 0);
      entry.totalDue += Number(bill.due_amount ?? bill.dueAmount ?? 0);

      const status = (bill.work_status || bill.workStatus || bill.payment_status || bill.status || 'other').toLowerCase();
      if (entry.statusCounts[status] !== undefined) {
        entry.statusCounts[status] += 1;
      } else {
        entry.statusCounts.other += 1;
      }

      if (Array.isArray(bill.services)) {
        bill.services.forEach((service) => {
          const serviceName = service.display_name || service.service_name || service.name || 'Unknown Service';
          if (!entry.services[serviceName]) {
            entry.services[serviceName] = {
              count: 0,
              serviceCharges: 0,
            };
          }
          entry.services[serviceName].count += Number(service.quantity || 1);
          const charge =
            Number(service.service_charge || service.serviceCharge || service.price || service.service_price || 0) ||
            Number(service.total || 0);
          entry.services[serviceName].serviceCharges += charge;
        });
      }
    });

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.dateKey).getTime() - new Date(a.dateKey).getTime()
    );
  }, [rawBills]);

  useEffect(() => {
    if (dailyData.length > 0 && !selectedDateKey) {
      setSelectedDateKey(dailyData[0].dateKey);
    }
  }, [dailyData, selectedDateKey]);

  const filteredDays = useMemo(() => {
    if (range === 'all') return dailyData;
    const limit = Number(range) || 30;
    return dailyData.slice(0, limit);
  }, [dailyData, range]);

  const totalBillCount = useMemo(
    () => filteredDays.reduce((sum, day) => sum + day.totalBills, 0),
    [filteredDays]
  );

  const totalServiceCharges = useMemo(
    () => filteredDays.reduce((sum, day) => sum + day.totalServiceCharges, 0),
    [filteredDays]
  );

  const totalPaid = useMemo(
    () => filteredDays.reduce((sum, day) => sum + day.totalPaid, 0),
    [filteredDays]
  );

  const totalDue = useMemo(
    () => filteredDays.reduce((sum, day) => sum + day.totalDue, 0),
    [filteredDays]
  );

  const grandTotal = useMemo(
    () => totalPaid + totalDue,
    [totalPaid, totalDue]
  );

  const selectedDay = useMemo(
    () => filteredDays.find((day) => day.dateKey === selectedDateKey) || filteredDays[0],
    [filteredDays, selectedDateKey]
  );

  const topServices = useMemo(() => {
    if (!selectedDay?.services) return [];
    return Object.entries(selectedDay.services)
      .map(([name, stats]) => ({
        name,
        ...stats,
      }))
      .sort((a, b) => b.serviceCharges - a.serviceCharges)
      .slice(0, 5);
  }, [selectedDay]);

  const maxBills = filteredDays.reduce((max, day) => Math.max(max, day.totalBills), 0);

  const handleCardClick = (event, day) => {
    const searchParams = new URLSearchParams({
      range: 'custom',
      from: day.dateKey,
      to: day.dateKey,
    });
    const url = `/bills/view?${searchParams.toString()}`;

    if (event.ctrlKey || event.metaKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  };

  const handleCardFocus = (day) => {
    setSelectedDateKey(day.dateKey);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
          color: 'white',
          boxShadow: '0 15px 45px rgba(15,23,42,0.35)',
        }}
      >
        <Typography variant="h4" fontWeight="700" gutterBottom>
          Counter Sales • Date Wise Dragging Model
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 760 }}>
          Scroll horizontally to review every day&apos;s counter performance. Click on any date card
          to instantly jump into the bill list for that specific day.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <TimelineIcon color="primary" />
          <Typography variant="body1" fontWeight={600}>
            Visible Range
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
          {RANGE_OPTIONS.map((option) => (
            <Chip
              key={option.id}
              label={option.label}
              color={range === option.id ? 'primary' : 'default'}
              onClick={() => setRange(option.id)}
              icon={<CalendarMonthIcon />}
              sx={{ minWidth: 110, fontWeight: 600 }}
              clickable
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchBills}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredDays.length === 0 && (
        <Alert severity="info" icon={<UpcomingIcon />}>
          No bills found yet. Create bills to start seeing day-wise counters here.
        </Alert>
      )}

      {!loading && !error && filteredDays.length > 0 && (
        <>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <VisibilityIcon color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    Scrollable Summary
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  divider={<Divider flexItem orientation="vertical" />}
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Days
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      {filteredDays.length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Counter Bills
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      {totalBillCount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Service Charges
                    </Typography>
                    <Typography variant="h5" fontWeight="700">
                      {formatCurrency(totalServiceCharges)}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                divider={<Divider flexItem orientation="vertical" />}
                sx={{ width: '100%', mt: 2 }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="success.main">
                    {formatCurrency(totalPaid)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Due
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="warning.main">
                    {formatCurrency(totalDue)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Grand Total (Paid + Due)
                  </Typography>
                  <Typography variant="h5" fontWeight="700" color="primary.main">
                    {formatCurrency(grandTotal)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 1,
              pt: 1,
              scrollSnapType: 'x proximity',
            }}
          >
            {filteredDays.map((day) => {
              const intensity = maxBills ? day.totalBills / maxBills : 0;
              const highlight = day.dateKey === selectedDateKey;
              return (
                <Card
                  key={day.dateKey}
                  onClick={(event) => handleCardClick(event, day)}
                  onMouseEnter={() => handleCardFocus(day)}
                  onFocus={() => handleCardFocus(day)}
                  tabIndex={0}
                  sx={{
                    minWidth: 240,
                    maxWidth: 260,
                    borderRadius: 3,
                    cursor: 'pointer',
                    scrollSnapAlign: 'center',
                    background: highlight
                      ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                      : `linear-gradient(135deg, rgba(59,130,246,${0.15 +
                          intensity * 0.35}), rgba(59,130,246,0.05))`,
                    color: highlight ? '#fff' : 'inherit',
                    boxShadow: highlight ? '0 20px 30px rgba(79,70,229,0.35)' : '0 8px 20px rgba(15,23,42,0.15)',
                    border: highlight ? '2px solid rgba(255,255,255,0.4)' : '1px solid rgba(59,130,246,0.2)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                    },
                    outline: 'none',
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <CalendarMonthIcon fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>
                        {formatDisplayDate(day.dateKey)}
                      </Typography>
                    </Stack>
                    <Typography variant="h3" fontWeight={800}>
                      {day.totalBills}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: highlight ? 0.9 : 0.75 }}>
                      Total counter bills
                    </Typography>
                    <Divider sx={{ my: 1.5, borderColor: highlight ? 'rgba(255,255,255,0.3)' : 'rgba(15,23,42,0.08)' }} />
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ReceiptLongIcon fontSize="small" />
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(day.totalServiceCharges)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TrendingUpIcon fontSize="small" />
                        <Typography variant="body2">
                          {day.grossAmount > 0 ? formatCurrency(day.grossAmount) : '—'} gross
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(day.statusCounts)
                        .filter(([, value]) => value > 0)
                        .map(([status, value]) => (
                          <Chip
                            key={status}
                            size="small"
                            icon={
                              status === 'completed' ? (
                                <DoneAllIcon fontSize="small" />
                              ) : status === 'pending' ? (
                                <PendingActionsIcon fontSize="small" />
                              ) : status === 'under_processing' ? (
                                <TimelineIcon fontSize="small" />
                              ) : (
                                <UpcomingIcon fontSize="small" />
                              )
                            }
                            label={`${status.replace('_', ' ')} • ${value}`}
                            sx={{
                              color: highlight ? '#fff' : 'inherit',
                              bgcolor: highlight ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)',
                              '& .MuiChip-icon': {
                                color: highlight ? '#fff' : 'inherit',
                              },
                            }}
                          />
                        ))}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {selectedDay && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 12px 30px rgba(15,23,42,0.15)' }}>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TodayIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>
                        {formatDisplayDate(selectedDay.dateKey)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Focused day summary. Tap &quot;Open bill list&quot; for complete details.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Counter bills
                        </Typography>
                        <Typography variant="h4" fontWeight={800}>
                          {selectedDay.totalBills}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Service charges
                        </Typography>
                        <Typography variant="h4" fontWeight={800}>
                          {formatCurrency(selectedDay.totalServiceCharges)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Gross amount
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {selectedDay.grossAmount > 0 ? formatCurrency(selectedDay.grossAmount) : 'N/A'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      variant="contained"
                      startIcon={<ViewTimelineIcon />}
                      sx={{ mt: 3 }}
                      onClick={(event) => handleCardClick(event, selectedDay)}
                    >
                      Open bill list
                    </Button>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      Top services
                    </Typography>
                    {topServices.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Service level breakdown not available.
                      </Typography>
                    )}
                    <Stack spacing={1.5}>
                      {topServices.map((service) => (
                        <Box
                          key={service.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid rgba(15,23,42,0.08)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {service.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty {service.count.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                          <Typography variant="body1" fontWeight={700}>
                            {formatCurrency(service.serviceCharges)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default CounterSalesDatewise;























